import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import '../styles/Dashboard.css';

const Dashboard = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('history');
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [profileSuccess, setProfileSuccess] = useState(null);
  const [profileError, setProfileError] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    if (user) {
      setProfileData((prev) => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const fetchEnrolledCourses = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await api.get('/users/me/courses');
      setEnrolledCourses(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setEnrolledCourses([]);
      setError(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'history' && isAuthenticated) {
      fetchEnrolledCourses();
    }
  }, [activeTab, isAuthenticated]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileSuccess(null);
    setProfileError(null);

    if (profileData.newPassword) {
      if (!profileData.currentPassword) {
        setProfileError('현재 비밀번호를 입력해주세요.');
        return;
      }
      if (profileData.newPassword !== profileData.confirmPassword) {
        setProfileError('새 비밀번호와 확인 비밀번호가 일치하지 않습니다.');
        return;
      }
      if (profileData.newPassword.length < 6) {
        setProfileError('비밀번호는 최소 6자 이상이어야 합니다.');
        return;
      }
    }

    try {
      const updateData = { name: profileData.name };
      if (profileData.newPassword) {
        updateData.currentPassword = profileData.currentPassword;
        updateData.newPassword = profileData.newPassword;
      }
      await api.put('/users/me', updateData);
      setProfileSuccess('프로필이 업데이트되었습니다.');
      setProfileData({
        ...profileData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      setProfileError(err.response?.data?.msg || '프로필 업데이트 중 오류가 발생했습니다.');
    }
  };

  if (loading || !user) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>로딩 중...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container dashboard-portal">
      <div className="dashboard-header">
        <p className="service-kicker">My page</p>
        <h1>마이페이지</h1>
        <p>
          안녕하세요, {user.name}님. 강의 판매는 중단되었고, 여기서는 계정과 이전 수강 기록만 관리합니다.
        </p>
      </div>

      <div className="service-notice">
        <strong>서비스 전환</strong>
        <span>신규 결제·수강 등록 없음 · 정보는 가이드 / 루트 파인더에서 제공</span>
        <div className="service-cta-row">
          <Link to="/guides" className="portal-btn portal-btn-primary">가이드</Link>
          <Link to="/finder" className="portal-btn portal-btn-ghost">루트 찾기</Link>
        </div>
      </div>

      <div className="dashboard-tabs">
        <button
          type="button"
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          이전 수강 내역
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          프로필
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {activeTab === 'history' && (
        <div className="my-courses-section">
          <h2>이전 수강·결제 기록</h2>
          <p className="section-help">
            예전에 등록된 강의가 있으면 아래에 표시됩니다. 새 결제는 할 수 없습니다.
          </p>

          {isLoading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>기록을 불러오는 중...</p>
            </div>
          ) : enrolledCourses.length === 0 ? (
            <div className="empty-courses">
              <p>보관된 수강 기록이 없습니다.</p>
              <Link to="/guides" className="btn btn-primary">
                입시 가이드 보기
              </Link>
            </div>
          ) : (
            <div className="courses-grid">
              {enrolledCourses.map((course) => (
                <div className="course-card" key={course._id}>
                  <div className="course-info">
                    <span className="archive-pill">아카이브</span>
                    <h3>{course.title}</h3>
                    <p className="course-category">{course.category}</p>
                    <div className="course-progress">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${course.progress || 0}%` }}
                        />
                      </div>
                      <span className="progress-text">{course.progress || 0}% 기록</span>
                    </div>
                    <Link to={`/learning/${course._id}`} className="btn btn-outline btn-sm">
                      기록 보기
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'profile' && (
        <div className="profile-section">
          <h2>프로필 관리</h2>
          {profileSuccess && <div className="success-message">{profileSuccess}</div>}
          {profileError && <div className="error-message">{profileError}</div>}

          <form onSubmit={handleProfileUpdate} className="profile-form">
            <div className="form-group">
              <label htmlFor="name">이름</label>
              <input
                type="text"
                id="name"
                name="name"
                value={profileData.name}
                onChange={handleProfileChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">이메일</label>
              <input type="email" id="email" name="email" value={profileData.email} disabled />
              <small>이메일은 변경할 수 없습니다.</small>
            </div>

            <h3>비밀번호 변경</h3>

            <div className="form-group">
              <label htmlFor="currentPassword">현재 비밀번호</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={profileData.currentPassword}
                onChange={handleProfileChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">새 비밀번호</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={profileData.newPassword}
                onChange={handleProfileChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">새 비밀번호 확인</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={profileData.confirmPassword}
                onChange={handleProfileChange}
              />
            </div>

            <button type="submit" className="btn btn-primary">
              프로필 업데이트
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
